
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ExternalLink, Briefcase, MapPin } from 'lucide-react';

const JobListing = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/careers`);
        if (!response.ok) {
          throw new Error('Failed to fetch jobs');
        }

        const allJobs = await response.json();

        // Sort by start date (most recent first)
        allJobs.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));

        setJobs(allJobs);
      } catch (error) {
        console.error('Error fetching jobs:', error);
        setError('Failed to load job opportunities. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, [API_BASE_URL]);

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-500/5 to-purple-500/5">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-slate-300 text-lg">Loading job opportunities...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-500/5 to-purple-500/5">
        <div className="text-center bg-red-500/10 border border-red-500/20 rounded-xl p-8 max-w-md">
          <div className="text-red-400 text-xl mb-2">⚠️</div>
          <p className="text-red-300">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500/5 to-purple-500/5 py-16 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Briefcase className="text-blue-400" size={32} />
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Available Opportunities
            </h1>
          </div>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Join our innovative team and help shape the future of drone technology
          </p>
        </div>

        {/* Job Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {jobs.length > 0 ? (
            jobs.map((job) => (
              <div
                key={job._id}
                className="group bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl overflow-hidden hover:border-blue-500/30 transition-all duration-300 hover:transform hover:-translate-y-2 hover:shadow-2xl hover:shadow-blue-500/10"
              >
                {job.imageUrl && (
                  <div className="relative overflow-hidden">
                    <img
                      src={job.imageUrl}
                      alt={job.title}
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
                  </div>
                )}

                <div className="p-6">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="p-2 bg-blue-500/10 rounded-lg">
                      <Briefcase className="text-blue-400" size={20} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-blue-400 transition-colors">
                        {job.title}
                      </h3>
                      <div className="flex items-center gap-2 text-slate-400 text-sm mb-3">
                        <MapPin size={14} />
                        <span>Remote/On-site</span>
                      </div>
                    </div>
                  </div>

                  <p className="text-slate-300 text-sm leading-relaxed mb-6 line-clamp-3">
                    {job.description}
                  </p>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-3 text-sm">
                      <div className="flex items-center gap-2 text-green-400">
                        <Calendar size={16} />
                        <span className="font-medium">Start:</span>
                      </div>
                      <span className="text-slate-300">{formatDate(job.startDate)}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <div className="flex items-center gap-2 text-red-400">
                        <Clock size={16} />
                        <span className="font-medium">End:</span>
                      </div>
                      <span className="text-slate-300">{formatDate(job.endDate)}</span>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-slate-700/50">
                    {job.googleFormLink ? (
                      <a
                        href={job.googleFormLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/25"
                      >
                        <ExternalLink size={18} />
                        Apply via Google Form
                      </a>
                    ) : (
                      <Link
                        to={`/apply/${job._id}`}
                        className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/25"
                      >
                        <Briefcase size={18} />
                        Apply Now
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full">
              <div className="text-center bg-slate-800/30 border border-slate-700/50 rounded-2xl p-12">
                <div className="text-6xl mb-4">🚀</div>
                <h3 className="text-2xl font-semibold text-white mb-4">No Opportunities Available</h3>
                <p className="text-slate-400 mb-2">We're currently not hiring, but exciting opportunities are coming soon!</p>
                <p className="text-slate-500 text-sm">Check back later for new openings.</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer Section */}
        <div className="text-center">
          <button
            className="bg-slate-700/50 text-slate-400 font-semibold py-4 px-8 rounded-xl border border-slate-600/50 cursor-not-allowed"
            disabled
          >
            <div className="flex items-center gap-2">
              <Clock size={20} />
              More Opportunities Coming Soon
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobListing;
